import { Button, Flex, Text } from '@sendoutcards/quantum-design-ui'
import React from 'react'
import { Icon } from 'src/design_system/atoms/icons/components/Icon'

type PlanDetail = {
  detail: string
}

type FullImpactPackageTileProps = {
  icon: React.ReactNode
  title: string
  price?: string
  details: PlanDetail[]
  onAddToCart: () => void
  gradient: string
  isMobile: boolean
  isTablet: boolean
}

export const FullImpactPackageTile: React.FC<FullImpactPackageTileProps> = ({
  icon,
  title,
  price,
  details,
  onAddToCart,
  gradient,
  isMobile,
  isTablet,
}) => {
  const formattedPrice = price?.trim() ?? ''
  const priceMatch = formattedPrice.match(/^\$?(\d+)(?:\.(\d+))?$/)
  const priceParts = priceMatch
    ? {
        currency: formattedPrice.startsWith('$') ? '$' : '',
        whole: priceMatch[1],
        cents: priceMatch[2] ?? '00',
      }
    : null

  return (
    <Flex width="100%" justifyContent="center">
      <Flex
        width={isTablet ? '90%' : '960px'}
        maxWidth="840px"
        borderRadius="large"
        overflow="hidden"
        style={{
          boxShadow: '0px 4px 16px rgba(0,0,0,0.08)',
        }}
        flexDirection={isTablet ? 'column' : 'row'}
      >
        <Flex
          flexDirection="column"
          width={isTablet ? '100%' : '33.333%'}
          flexGrow={isTablet ? 1 : 0}
          alignItems="flex-start"
          backgroundColor="#FFFFFF"
          inset={{ horizontal: 'x4', vertical: 'x5' }}
          rowGap="x1_5"
          style={{
            minWidth: isTablet ? 'auto' : '380px',
            maxWidth: isTablet ? '100%' : '33.333%',
          }}
        >
          {isMobile ? (
            <Flex alignItems="center" flexDirection="row">
              {icon}
              <Text
                type="subtitle"
                color="primaryHeading"
                content={title}
                style={{
                  fontSize: 40,
                  lineHeight: 1.25,
                  fontWeight: 400,
                  color: '#6B7280',
                  marginLeft: 8,
                }}
              />
            </Flex>
          ) : (
            <>
              <Flex>{icon}</Flex>
              <Text
                type="subtitle"
                color="primaryHeading"
                content={title}
                style={{
                  fontSize: 40,
                  lineHeight: 1.3,
                  fontWeight: 400,
                  color: '#6B7280',
                }}
              />
            </>
          )}
          {price && (
            <Flex
              alignItems="flex-end"
              style={{ columnGap: 2, marginTop: 'auto', marginLeft: '36px' }}
              inset={{ top: 'x4' }}
            >
              {priceParts ? (
                <>
                  <Text
                    type="title"
                    color="primaryBody"
                    style={{
                      fontSize: 40,
                      lineHeight: 1.05,
                      fontWeight: 300,
                      color: '#6B7280',
                    }}
                  >
                    {`${priceParts.currency}${priceParts.whole}`}
                  </Text>
                  <Text
                    type="body"
                    color="primaryBody"
                    style={{
                      fontSize: 16,
                      lineHeight: 1.05,
                      fontWeight: 300,
                      color: '#6B7280',
                    }}
                  >
                    {`.${priceParts.cents}`}
                  </Text>
                </>
              ) : (
                <Text
                  type="title"
                  color="primaryBody"
                  style={{
                    fontSize: 40,
                    lineHeight: 1.05,
                    fontWeight: 300,
                    color: '#6B7280',
                  }}
                >
                  {price}
                </Text>
              )}
            </Flex>
          )}
          <Flex width="100%" inset={{ top: 'x2' }} justifyContent="center">
            <Flex width="100%" maxWidth="240px" justifyContent="center">
              <Button
                title="Add to Cart"
                onClick={onAddToCart}
                fullWidth
                textColorOverride="#FFFFFF"
                backgroundColorOverride={gradient}
              />
            </Flex>
          </Flex>
        </Flex>
        <Flex
          flexDirection="column"
          justifyContent="center"
          width={isTablet ? '100%' : '66.667%'}
          inset={{
            horizontal: isMobile ? 'x3' : 'x5',
            vertical: isMobile ? 'x3' : 'x5',
          }}
          rowGap={isMobile ? 'x1_5' : 'x2'}
          style={{
            background: gradient,
            color: '#FFFFFF',
            maxWidth: isTablet ? '100%' : '66.667%',
          }}
        >
          {details.map((detail, index) => (
            <Flex key={`full-impact-feature-${index}`} alignItems="center">
              <Icon name="v_check" color="#FFFFFF" size={isMobile ? 16 : 20} />
              <Text
                type={isMobile ? 'body' : 'largeBody'}
                color="accent"
                style={{
                  color: '#FFFFFF',
                  fontSize: isMobile ? 13 : undefined,
                }}
                outset={{ left: 'x1' }}
              >
                {detail.detail}
              </Text>
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Flex>
  )
}
